import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import axios from "../../../api/axios";
import toast from "../../sweetalert/Sweetalert";
import { destroySession } from "../../../helpers/Session";
import { useNavigate } from "react-router-dom";
import axiosaim from "../../../api/axiosaim";

const Timesheet = ({ departmentname, departmentslug, employeeUname }) => {
    const APPROVER_TYPE = 1;

    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const timeoutRef = useRef(null);

    const accesstoken = sessionStorage.getItem('accesstoken') || '';
    const aimtoken = sessionStorage.getItem('aimtoken') || '';
    const creator = sessionStorage.getItem('uname') || '';
    
    const [employeeList, setEmployeeList] = useState([]);
    const [loadingTimesheetApprovers, setLoadingTimesheetApprovers] = useState(false);

    const APPROVER_QUERY_KEY = ['departmentTimesheetApprovers', { departmentslug, APPROVER_TYPE, employeeUname }];

    const { data: employees } = useQuery({
        queryKey: ['departmentEmployees', {departmentslug}],
        queryFn: async () => {
            const res = await axiosaim.get(`getuserlistsperdepartment?d=${departmentslug}`, {
                headers: { 'X-Aim-Token': aimtoken }
            });
            return res.data;
        },
        enabled: !!departmentslug && !!aimtoken,
    });

    const { data: timesheetApprovers, isLoading: isLoadingTimesheetApprovers, error: errorTimesheetApprovers } = useQuery({
        queryKey: APPROVER_QUERY_KEY,
        queryFn: async () => {
            if (!employeeUname) return [];
            try {
                const res = await axios.get(`approvals?department=${departmentslug}&type=${APPROVER_TYPE}&employee=${employeeUname}`, {
                    headers: { 'Authorization': `Bearer ${accesstoken}` }
                });
                setLoadingTimesheetApprovers(false);
                return res.data.response;
            } catch (error) {
                setLoadingTimesheetApprovers(false);
            }
        },
        onError: (error) => {
            setLoadingTimesheetApprovers(false);
        },
        onSuccess: () => {
            setLoadingTimesheetApprovers(false);
        },
        enabled: !!departmentslug && !!accesstoken && !!employeeUname,
        staleTime: 0,
        retry: false
    });
    

    useEffect(() => {
        if (timesheetApprovers && timesheetApprovers.length > 0) {
            setEmployeeList(timesheetApprovers);
        } else {
            setEmployeeList([]);
        }
    }, [timesheetApprovers]);

    useEffect(() => {
        if (errorTimesheetApprovers) {
            if (errorTimesheetApprovers.response?.status === 404) {
                setEmployeeList([]);
            } else {
                setEmployeeList([]);
            }
        }
    }, [errorTimesheetApprovers]);

    const handleAddEmployee = () => {
        const newEmployee = {
            id: `employee-${employeeList?.length + 1}`,
            sequence: employeeList?.length + 1,
            uname: `employee-${employeeList?.length + 1}`,
            fname: '',
            lname: '',
            email: '',
            status: true
        };
        setEmployeeList(prevState => [...prevState, newEmployee]);
    };

    const departmentTimesheetApproverMutation = useMutation({
        mutationFn: async (approverData) => {
            const res = await axios.post('approvals', approverData, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data;
        },
        onSuccess: (data) => {
            toast.fire({ icon: 'success', title: data.response });
            queryClient.invalidateQueries(APPROVER_QUERY_KEY);
        },
        onError: (error) => {
            const errorMessage = error.response?.data.response || "Something went wrong while saving timesheet approver details!";
            toast.fire({ icon: 'error', title: errorMessage });

            if (error.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });

    const handleApproverStatus = (id, sequence) => {
        setEmployeeList(prevState =>
            prevState.map(emp =>
                emp.sequence === sequence
                ? { ...emp, status: !emp.status }
                : emp
            )
        );
        const employee = employeeList?.find(item => item.id === id);
        
        if (employee && employee.fname !== "") {
            departmentTimesheetApproverMutation.mutate({
                id: employee.id,
                department: departmentname,
                departmentcode: departmentslug,
                uname: employee.uname,
                name: employee.name,
                email: employee.email,
                sequence: employee.sequence,
                status: !employee.status,
                type: APPROVER_TYPE,
                creator,
                employee: employeeUname
            });
        }
    };

    const handleSetApprover = (e, id, sequence) => {
        const uname = e.target.value;
        
        if (uname === "") {
            setEmployeeList(prevState => prevState.filter(emp => emp.sequence !== sequence));
        } else {
            const selectedEmployee = employees?.find(item => item.uname === uname);
            if (selectedEmployee) {
                const updatedEmployeeList = employeeList?.map(emp =>
                    emp.sequence === sequence
                        ? { ...emp, ...selectedEmployee }
                        : emp
                );
                setEmployeeList(updatedEmployeeList);
                
                const employee = updatedEmployeeList?.find(item => item.id === id);
                departmentTimesheetApproverMutation.mutate({
                    id: employee.id,
                    department: departmentname,
                    departmentcode: departmentslug,
                    uname: employee.uname,
                    name: `${employee.fname} ${employee.lname}`,
                    email: employee.email,
                    sequence: employee.sequence,
                    status: 1,
                    type: APPROVER_TYPE,
                    creator,
                    employee: employeeUname
                });
            }
        }
    };

    const departmentApproverDeleteMutation = useMutation({
        mutationFn: async (id) => {
            const res = await axios.delete(`approvals/${id}?uname=${creator}&employee=${employeeUname}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data;
        },
        onSuccess: (data) => {
            toast.fire({ icon: 'success', title: data.response });
            queryClient.invalidateQueries(APPROVER_QUERY_KEY);
        },
        onError: (error) => {
            const errorMessage = error.response?.data.response || "Something went wrong while deleting timesheet approver details!";
            toast.fire({ icon: 'error', title: errorMessage });

            if (error.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });

    const handleRemoveEmployee = (id) => {
        const employee = employeeList?.find(item => item.id === id);

        if (employee && employee.fname !== "") {
            departmentApproverDeleteMutation.mutate(employee.id, creator);
        }
        
        setEmployeeList(prevState => prevState.filter(emp => emp.id !== id));
    };

    return (
        <div className="content">
            {isLoadingTimesheetApprovers || loadingTimesheetApprovers ? (
                [...Array(2)].map((_, i) => (
                    <div key={i} className="employee loading">
                        <div>
                            <div className="top">
                                <p>Approver 1</p>
                            </div>
                            <select>
                                <option value="ralejo">Resty Alejo</option>
                            </select>
                        </div>
                    </div>
                ))
            ) : (
                <>
                    {employeeList?.map((employee, index) => (
                        <div className={`employee ${employee.status ? '' : 'inactive'}`} key={index}>
                            <div>
                                <div className="top">
                                    <p>{`Approver ${employee.sequence}`}</p>
                                    <div className="switch">
                                        <input
                                        type="checkbox"
                                        id={`toggle-${index + 1}`}
                                        className="switch-input"
                                        title="Active/Inactive"
                                        checked={employee.status}
                                        onChange={() => handleApproverStatus(employee.id, index + 1)}
                                        />
                                        <label htmlFor={`toggle-${index + 1}`} className="switch-label"></label>
                                    </div>
                                </div>
                                <select onChange={(e) => handleSetApprover(e, employee.id, index + 1)} value={employee.uname}>
                                    {employees?.length > 0 ? 
                                        (
                                            <>
                                                <option value=""></option>
                                                {employees?.reduce((acc, { department, uname, fname, lname, position_title, employee_id }) => {
                                                    if (!acc[department]) acc[department] = [];
                                                    acc[department].push({ uname, fname, lname, position_title, employee_id });
                                                    return acc;
                                                }, {}) && Object.entries(employees?.reduce((acc, { department, uname, fname, lname, position_title, employee_id }) => {
                                                    if (!acc[department]) acc[department] = [];
                                                    acc[department].push({ uname, fname, lname, position_title, employee_id });
                                                    return acc;
                                                }, {})).map(([department, employees]) => (
                                                    <optgroup key={department} label={department}>
                                                    {employees.map(({ uname, fname, lname }) => (
                                                        <option key={uname} value={uname}>
                                                        {`${fname} ${lname}`}
                                                        </option>
                                                    ))}
                                                    </optgroup>
                                                ))}
                                            </>
                                        ) : (
                                            employeeList?.length > 0 && (
                                                <option key={employee.uname} value={employee.uname}>
                                                    {employee.name}
                                                </option>
                                            )
                                        )
                                    }
                                    
                                    {employeeList?.length > 0 && employee?.length > 0 && !employees?.some(emp => emp.uname === employee?.uname) && (
                                        <option key={employee?.uname} value={employee?.uname}>
                                            {employee?.name}
                                        </option>
                                    )}
                                </select>
                            </div>
                            {index > 0 && (
                                <button type="button" className="btn-remove-employee" title="REMOVE" onClick={() => handleRemoveEmployee(employee.id)}>
                                    <i className="fa fa-trash"></i>
                                </button>
                            )}
                        </div>
                    ))}

                    <div className="button-group">
                        {employeeList?.length < employees?.length && (
                            <button type="button" className="btn-add-employee" onClick={handleAddEmployee}>
                                <i className="fa fa-plus"></i>
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default Timesheet;
